import styles from './InitBooking.module.scss'
import cmsContactBlockSettingsObject from '@/entities/cmsContactBlockSettings/interface'
import { useTranslation } from 'next-i18next'
import { TravelIdContext } from '@/app/context'
import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import TravelDateSelector from './TravelDateSelector'
import TravellerSelector from './TravellerSelector'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { getStateBooking, initBooking, selectBookings, setCurrentBookingStep } from '@/features/booking'
import Link from 'next/link'
import TransportTypeSelector from './TransportTypeSelector'
import { bookingStateItem } from '@/features/booking/types'
import NotBookableInfo from '@/components/models/booking/NotBookableInfo'
import AvailabilityStatusInfo from './AvailabilityStatusInfo'
import LocalBooking from '@/lib/booking/LocalBooking'
import { useContext, useEffect } from 'react'
import InitBookingLoadingPlaceholder from './InitBookingLoadingPlaceholder'
import StartBookingButtonLead from '@/components/models/booking/InitBooking/buttons/StartBookingButtonLead'

export interface InitBookingProps {
  contactBlockSettings: cmsContactBlockSettingsObject
  colorTheme?: 'light' | 'dark'
  navType?: 'wizard' | 'lead' // wizard = embedded in booking wizard | lead = embedded in other page
  setMobileOpen?: (open: boolean) => void
}

const InitBooking = ({ contactBlockSettings, colorTheme = 'light', setMobileOpen, navType = 'wizard' }: InitBookingProps) => {
  const { t } = useTranslation('common')

  const travelId = useContext(TravelIdContext)
  const dispatch = useAppDispatch()
  const bookings = useAppSelector(selectBookings)
  const booking = getStateBooking(bookings, travelId as number) as bookingStateItem
  const manager = new LocalBooking(booking)

  useEffect(() => {
    dispatch(initBooking({ travelId: travelId as number }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStartBooking = () => {
    dispatch(setCurrentBookingStep({ travelId: travelId as number, step: 0 }))
  }

  if (!booking || !booking.travel) {
    return <InitBookingLoadingPlaceholder navType={navType} />
  }

  if (manager.getAvailabilityStatus().status === 'CLOSED') {
    return (
      <div className={`${styles.root} ${styles.wrapSpacingTop} px-0`}>
        <NotBookableInfo availabilityStatus={manager.getAvailabilityStatus()} contactBlockSettings={contactBlockSettings} />
      </div>
    )
  }

  return (
    <>
      <div>
        {booking && booking.travel && (
          <div className={`${styles.root} ${styles[navType]}`}>
            {setMobileOpen && (
              <div className={`${styles.mobileNav} d-lg-none`}>
                <Button variant={'outline-light'} className={'text-uppercase'} onClick={() => setMobileOpen(false)}>
                  {t('button.close')}
                </Button>
              </div>
            )}
            <div className={styles.header}>
              <div className={styles.lead}>{t('booking.startPlanTravel')}</div>
              <div className={styles.title}>{booking.travel.name}</div>
              <AvailabilityStatusInfo availabilityStatusTenantSettings={manager.getAvailabilityStatusTenantSettings()} />
            </div>
            <Container fluid className={`p-0 ${styles.basePrice}`}>
              <Row className="justify-content-between align-items-baseline mb-4">
                <Col xs={'auto'} className={styles.label}>
                  {t('booking.pricePerPerson')}
                </Col>
                <Col className="text-end">
                  <span className={styles.from}>{t('booking.priceFrom')}&nbsp;</span>
                  <span className={styles.price}>{manager.formatPrice(booking.travel.minPrice)}</span>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <TransportTypeSelector pricingTravels={booking.travel.pricing.travels} colorTheme={colorTheme} />
                </Col>
                <Col xs={12}>
                  <TravelDateSelector colorTheme={colorTheme} />
                </Col>
                <Col xs={12}>
                  <TravellerSelector ageGroupSettings={booking.travel.pricing.ageGroupSettings} colorTheme={colorTheme} />
                </Col>
              </Row>
              {navType === 'lead' ? (
                <Row className={'mb-1'}>
                  <Col>
                    <Link href={`/booking/${booking.travel.seoSettings.urlSlug}`} passHref>
                      <StartBookingButtonLead
                        onClick={handleStartBooking}
                        disabled={booking.travelDate === undefined || !booking.travellers?.length}
                      />
                    </Link>
                  </Col>
                </Row>
              ) : (
                <>
                  <Row className="justify-content-between mt-4">
                    <Col xs={'auto'} />
                    <Col xs={'auto'}>
                      <Button
                        variant={'none'}
                        className={'btn-booking-step-next'}
                        onClick={handleStartBooking}
                        disabled={booking.travelDate === undefined || !booking.travellers?.length}
                      >
                        {t('booking.next')}
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
              {/*<Row className={'mb-5'}>*/}
              {/*  <Col>*/}
              {/*    <Accordion*/}
              {/*      variant={'unstyledWhite'}*/}
              {/*      className={styles.accordion}*/}
              {/*      items={[*/}
              {/*        { title: t('travel.pricing.includedServices'), content: <div dangerouslySetInnerHTML={{ __html: travel.includedServices }} /> },*/}
              {/*      ]}*/}
              {/*    />*/}
              {/*  </Col>*/}
              {/*</Row>*/}
            </Container>
          </div>
        )}
      </div>
      <style global jsx>
        {`
          @media screen and (max-width: 992px) {
            .glob-cookie-banner {
              bottom: 58px;
            }
          }
        `}
      </style>
    </>
  )
}

export default InitBooking
